/* Adaptr Orbit — seed/mock data, ported from the prototype's ui.jsx.
   This is the INITIAL render data. Live wiring (useMe, lessons, Autumn,
   user_memory) replaces these incrementally — keep the shapes stable so a
   fetch result can drop in where a constant is imported today. */

export const fmt = (n: number) => n.toLocaleString("en-US");

export const USER = {
  name: "Alex Chen",
  initials: "AC",
  email: "alex@adaptr.studio",
  plan: "Pro",
  role: "Senior Product Designer",
};

export const PLAN = {
  name: "Orbit Pro",
  monthly: 20,
  annualPerMo: 16,
  allowance: 5000,
  renew: "Jul 1",
  daysLeft: 12,
  card: { brand: "Visa", last4: "4242", exp: "04 / 27" },
};

export const USAGE = {
  used: 4640,
  sonnet: 2360,
  opus: 2280,
  streak: 11,
  activeDays: 24,
  taken: 12,
  mastered: 4,
  days: [5, 7, 4, 8, 6, 3, 7, 9, 6, 8, 7, 5, 8, 9],
};

export const INVOICES = [
  { date: "Jun 1, 2026", amt: "$20.00", note: "Orbit Pro — monthly", status: "Paid" },
  { date: "May 14, 2026", amt: "$11.00", note: "Credit top-up — 2,500", status: "Paid" },
  { date: "May 1, 2026", amt: "$20.00", note: "Orbit Pro — monthly", status: "Paid" },
  { date: "Apr 1, 2026", amt: "$20.00", note: "Orbit Pro — monthly", status: "Paid" },
  { date: "Mar 1, 2026", amt: "$20.00", note: "Orbit Pro — monthly", status: "Paid" },
];

export const TOPUPS = [
  { credits: 1000, price: 5 },
  { credits: 2500, price: 11, tag: "Popular" },
  { credits: 6000, price: 24, tag: "Best value" },
];

export const PROFILE = {
  responsibilities: ["Design-system ownership", "Prototyping & flows", "Design reviews & critique", "Mentoring 2 juniors"],
  stack: ["Figma", "Linear", "Notion", "GitHub", "Slack", "Loom", "Framer"],
  time: [
    { label: "Design & prototyping", pct: 45 },
    { label: "Meetings & reviews", pct: 25 },
    { label: "Docs & specs", pct: 18 },
    { label: "Eng handoff", pct: 12 },
  ],
  notes: "Prefers concise async updates. Working toward leading AI-assisted design ops for the team.",
  memory: [
    { t: "Owns the Adaptr Human design system", ic: "box" },
    { t: "Prefers Sonnet for quick asks, Opus for planning", ic: "sparkles" },
    { t: "Usually works 9–6 PT, deep-focus mornings", ic: "clock" },
    { t: "Goal: get fluent at AI-assisted prototyping", ic: "flag" },
  ],
};

export type LessonState = "mastered" | "active" | "ready" | "locked";
export type Lesson = { name: string; state: LessonState; pct: number; dur: string; level: string; desc: string };
export type Track = { group: string; icon: string; lessons: Lesson[] };

export const TRACKS: Track[] = [
  {
    group: "Tools",
    icon: "wrench",
    lessons: [
      { name: "Figma AI — first draft to polish", state: "mastered", pct: 100, dur: "8 min", level: "Core", desc: "Generate first drafts and refine them to production polish." },
      { name: "Claude for design critique", state: "active", pct: 60, dur: "10 min", level: "Core", desc: "Get sharp, principle-based critique on any screen." },
      { name: "Cursor for design tokens", state: "ready", pct: 0, dur: "12 min", level: "Intermediate", desc: "Generate and wire design tokens straight into code." },
      { name: "Linear AI triage", state: "ready", pct: 0, dur: "6 min", level: "Core", desc: "Auto-triage, label and summarize incoming issues." },
      { name: "Notion Q&A over specs", state: "locked", pct: 0, dur: "7 min", level: "Intermediate", desc: "Ask questions across all your specs and docs." },
    ],
  },
  {
    group: "Skills",
    icon: "brain",
    lessons: [
      { name: "Prompting fundamentals", state: "mastered", pct: 100, dur: "9 min", level: "Core", desc: "The patterns behind every reliable prompt." },
      { name: "Context engineering", state: "active", pct: 35, dur: "14 min", level: "Core", desc: "Give models the right context, every time." },
      { name: "Evaluating AI output", state: "ready", pct: 0, dur: "11 min", level: "Intermediate", desc: "Judge quality against your own rubric, not vibes." },
      { name: "Designing agent workflows", state: "locked", pct: 0, dur: "16 min", level: "Advanced", desc: "Compose multi-step agentic flows that hold up." },
    ],
  },
];

export type SkillPlanItem = { t: string; d: string; st: "approved" | "pending" | "denied" };
export const SKILL_PLAN: SkillPlanItem[] = [
  { t: "Context engineering for design briefs", d: "Structure briefs so Claude returns usable flows first time.", st: "approved" },
  { t: "Prompt patterns for UI critique", d: "Reusable prompts that surface real accessibility & hierarchy issues.", st: "pending" },
  { t: "Building eval rubrics", d: "Judge AI output against your design principles, not vibes.", st: "pending" },
  { t: "Agentic prototyping in Cursor", d: "Drive a coding agent to working prototypes from a spec.", st: "pending" },
  { t: "Token systems with AI", d: "Generate and audit design tokens at scale.", st: "pending" },
];

export const UPCOMING = [
  { t: "Claude for design critique", st: "active" },
  { t: "Context engineering", st: "active" },
  { t: "Evaluating AI output", st: "queued" },
  { t: "Designing agent workflows", st: "skipped" },
];

export const INTEGRATIONS = [
  { name: "Gmail", icon: "mail", desc: "Draft & summarize email in context", on: true },
  { name: "Google Calendar", icon: "calendar", desc: "Prep, schedule and follow-ups", on: true },
  { name: "Google Docs", icon: "file-text", desc: "Q&A and drafting over your docs", on: true },
  { name: "GitHub", icon: "github", desc: "PRs, issues and code context", on: false },
  { name: "Slack", icon: "slack", desc: "Summaries and quick replies", on: false },
  { name: "Linear", icon: "git-branch", desc: "Triage and write issues", on: false },
];

export const MCP = [
  { name: "Figma MCP", url: "figma.mcp.adaptr.studio", on: true },
  { name: "Internal wiki", url: "wiki.mcp.adaptr.studio", on: true },
];

export const PROJECTS = [
  { name: "Adaptr Human DS", desc: "Tokens, components, usage docs", files: 28 },
  { name: "Orbit redesign", desc: "Nav, billing, onboarding", files: 12 },
];

export const TEAM = [
  { name: "Alex Chen", email: "alex@adaptr.studio", role: "Owner", initials: "AC", you: true },
  { name: "Jordan Lee", email: "jordan@adaptr.studio", role: "Admin", initials: "JL" },
  { name: "Sam Rivera", email: "sam@adaptr.studio", role: "Member", initials: "SR" },
  { name: "Priya Nair", email: "priya@adaptr.studio", role: "Member", initials: "PN", pending: true },
];

export type Achievement = { t: string; ic: string; done: boolean; prog?: number; goal?: number };
export const ACHIEVEMENTS: Achievement[] = [
  { t: "First lesson", ic: "footprints", done: true },
  { t: "3-day streak", ic: "flame", done: true },
  { t: "Tool master: Figma AI", ic: "wand-2", done: true },
  { t: "Prompt pro", ic: "message-square-quote", done: true },
  { t: "10-day streak", ic: "flame", done: false, prog: 11, goal: 10 },
  { t: "Completed a track", ic: "trophy", done: false, prog: 1, goal: 2 },
  { t: "Curriculum complete", ic: "graduation-cap", done: false, prog: 4, goal: 12 },
  { t: "AI eval certified", ic: "badge-check", done: false, prog: 0, goal: 1 },
];

export const HISTORY = [
  { id: "seed-1", t: "Summarize this PR and flag risky changes", mode: "Helper", ago: "2h ago", ic: "git-pull-request" },
  { id: "seed-2", t: "Rewrite the onboarding empty-state copy", mode: "Helper", ago: "5h ago", ic: "pen-line" },
  { id: "seed-3", t: "Lesson: Context engineering — checkpoint", mode: "Coach", ago: "Yesterday", ic: "graduation-cap" },
  { id: "seed-4", t: "Draft a reply to the design review thread", mode: "Helper", ago: "Yesterday", ic: "mail" },
  { id: "seed-5", t: "Explain this Figma auto-layout bug", mode: "Helper", ago: "2 days ago", ic: "layout" },
  { id: "seed-6", t: "Lesson: Claude for design critique", mode: "Coach", ago: "3 days ago", ic: "graduation-cap" },
];

export const SAVED = [
  { t: "PR review prompt template", ic: "git-pull-request" },
  { t: "Empty-state copy — 3 variants", ic: "pen-line" },
  { t: "Design principles for eval rubric", ic: "scale" },
];
